import { SessionSummary } from '@bioliberty/lifemind';
import { Copy, Info, Sparkles } from 'lucide-react';
import posthog from 'posthog-js';
import { Button } from '../../../components/Button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../components/Dialog';
import { NoCapture } from '../../../components/NoCapture';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/Tooltip';
import { RegenerateSessionNoteButton } from '../../../home/clinician-dashboard/recent-sessions/RegenerateSessionNoteButton';
import { SessionNoteRating } from '../../../home/clinician-dashboard/recent-sessions/SessionNoteRating';
import { useToaster } from '../../../hooks/useToaster';
import { Event } from '../../../lib/posthog';

interface ViewSessionNoteModalProps {
  session: SessionSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ViewSessionNoteModal = ({ session, open, onOpenChange }: ViewSessionNoteModalProps) => {
  const toast = useToaster();

  const onCopyToClipboard = () => {
    navigator.clipboard.writeText(session.note ?? '');
    toast.success('Copied session summary to clipboard.');
    posthog.capture(Event.SessionNoteCopyButtonClick);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px]" data-testid="view-session-note-modal">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="to-accent flex items-center gap-1 bg-linear-to-r from-green-500 bg-clip-text font-bold text-transparent">
                    Session {session.sessionNumber} - AI Summary
                    <Sparkles className="text-accent h-4 w-4" />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="flex items-center gap-2">
                  <Info className="w-4" /> Uses AI. Please verify results.
                </TooltipContent>
              </Tooltip>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-96 overflow-auto">
          <NoCapture>{session.note}</NoCapture>
        </div>

        <DialogFooter>
          <div className="flex w-full flex-col gap-4 md:flex-row md:justify-between">
            <div className="flex flex-col gap-4 md:flex-row">
              <Button variant="accent" size="m" icon={Copy} onClick={onCopyToClipboard}>
                Copy to clipboard
              </Button>

              <RegenerateSessionNoteButton session={session} />
            </div>

            <SessionNoteRating session={session} />
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
